import {
    CloudDownload,
    WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { Box, ButtonBase, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ModalityChip from '~/components/Chip/ModalityChip';
import ModelDetails from '~/components/ModelDetails';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { formatSeriesDate } from '~/utils/date';
import { transform3DImageData } from '~/utils/transforms/imageDataTransforms';
import { describeUnloadedImage } from './describeUnloadedImage';
import ImageBadges from './ImageBadges';
import ImageInfoButton from './ImageInfoButton';
import UnloadedImageTooltip from './UnloadedImageTooltip';

const STATE_ICON_PX = 16;

const ROW_SX = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 0.5,
    minWidth: 0,
    p: 1,
} as const;

/**
 * The lines of an image row: the lead, the tag chip, the name and the
 * trailing values, then the badges. The badge line is left out while no
 * badge applies.
 */
function SummaryLines({
    lead,
    chip,
    name,
    trailing,
    badges,
}: {
    lead?: ReactNode;
    chip: ReactNode;
    name: ReactNode;
    trailing: ReactNode;
    badges?: ReactNode;
}) {
    return (
        <>
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                {lead}
                {chip}
                <Typography
                    component="span"
                    fontSize={13}
                    fontWeight={500}
                    lineHeight="20px"
                    noWrap
                    flex={1}
                    minWidth={0}
                >
                    {name}
                </Typography>
                <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    flexShrink={0}
                    fontSize={11}
                    color="text.secondary"
                    whiteSpace="nowrap"
                >
                    {trailing}
                </Box>
            </Box>
            <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={0.5}
                sx={{ '&:empty': { display: 'none' } }}
            >
                {badges}
            </Box>
        </>
    );
}

/**
 * One image of the patient on one line, two with badges: its tag chip, its
 * name, its series date and the (i) to its details, then its badges. An
 * image that is not loaded leads with what it needs: a download, which a
 * click on the row starts when `onDownload` is given, or a reformat, which
 * this row does not offer. Its properties show on hover. Names and dates
 * are PHI and masked.
 */
export default function ImageSummaryRow({
    image,
    hasSharedFrameOfReference,
    onDownload,
}: {
    image: AvailableImage;
    hasSharedFrameOfReference?: boolean;
    /** Downloads an image known only from the session. */
    onDownload?: () => void;
}) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const loaded = image.imageObject;
    const metadata = useMemo(
        () => (loaded ? transform3DImageData(loaded) : undefined),
        [loaded]
    );

    const nameOf = (description: string) => (
        <span title={showSensitiveInfo ? description : undefined}>
            <SensitiveInfoTypography>{description}</SensitiveInfoTypography>
        </span>
    );

    if (loaded && metadata) {
        const description =
            metadata.series.description ||
            intl.formatMessage({ id: 'unknown.series' });
        return (
            <Box sx={ROW_SX} data-testid={`image-item-${metadata.series.id}`}>
                <SummaryLines
                    chip={
                        <ModalityChip
                            modality={metadata.modality}
                            size="small"
                        />
                    }
                    name={nameOf(description)}
                    trailing={
                        <>
                            <SensitiveInfoTypography>
                                {formatSeriesDate(metadata.series.date)}
                            </SensitiveInfoTypography>
                            <ImageInfoButton
                                image={loaded}
                                hasSharedFrameOfReference={
                                    hasSharedFrameOfReference
                                }
                            />
                        </>
                    }
                    badges={<ImageBadges image={loaded} />}
                />
                <ModelDetails
                    fullWidth
                    generationModel={metadata.generationModel}
                />
            </Box>
        );
    }

    const { needsReformat, description, seriesDate } = describeUnloadedImage(
        image,
        intl
    );
    const lines = (
        <SummaryLines
            lead={
                needsReformat ? (
                    <WarningAmberIcon
                        sx={{ fontSize: STATE_ICON_PX, color: 'warning.main' }}
                    />
                ) : (
                    <CloudDownload
                        sx={{
                            fontSize: STATE_ICON_PX,
                            color: 'text.secondary',
                        }}
                    />
                )
            }
            chip={
                <ModalityChip
                    modality={image.modality}
                    size="small"
                    color={needsReformat ? 'warning' : 'default'}
                />
            }
            name={nameOf(description)}
            trailing={
                seriesDate && (
                    <SensitiveInfoTypography>
                        {formatSeriesDate(seriesDate)}
                    </SensitiveInfoTypography>
                )
            }
        />
    );

    return (
        <UnloadedImageTooltip image={image}>
            {!needsReformat && onDownload ? (
                <ButtonBase
                    onClick={onDownload}
                    disableRipple
                    sx={(theme) => ({
                        ...ROW_SX,
                        width: '100%',
                        textAlign: 'left',
                        font: 'inherit',
                        color: 'text.primary',
                        '&:hover, &.Mui-focusVisible': {
                            backgroundColor: theme.palette.action.hover,
                        },
                    })}
                >
                    {lines}
                </ButtonBase>
            ) : (
                <Box sx={ROW_SX}>{lines}</Box>
            )}
        </UnloadedImageTooltip>
    );
}
